"use client";

import React, { useEffect, useRef, useState } from "react";

interface HelpfulLinkPreviewProps {
  url: string;
}

interface LinkPreviewData {
  url: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  image: string | null;
  favicon: string | null;
  partial?: boolean;
}

function renderPreviewImage(
  data: LinkPreviewData | null,
  hostname: string,
  initials: string,
  gradientStyle: React.CSSProperties,
) {
  if (data?.image) {
    return (
      <div className="w-28 h-28 flex-shrink-0 bg-default-100">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt="Preview image"
          className="w-full h-full object-cover"
          src={data.image}
        />
      </div>
    );
  }

  return (
    <div
      aria-label={`${hostname} placeholder`}
      className="w-28 h-28 flex-shrink-0 relative flex items-center justify-center"
      style={gradientStyle}
    >
      <span className="text-default-700 font-semibold text-lg tracking-wide">
        {initials}
      </span>
      {data?.favicon && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          alt="favicon"
          className="w-5 h-5 absolute bottom-1 right-1 rounded-sm shadow-sm bg-white/70"
          src={data.favicon}
        />
      )}
    </div>
  );
}

function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function getDomainInitials(domain: string): string {
  const base = domain.split(".")[0] || domain;
  const letters = base.replace(/[^a-z0-9]/gi, "").toUpperCase();

  if (!letters) return "?";

  return letters.slice(0, 2);
}

function hashStringToHue(input: string): number {
  let hash = 0;

  for (let i = 0; i < input.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + input.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }

  return Math.abs(hash) % 360;
}

async function retryFetchPreview(
  normalizedUrl: string,
  isMountedRef: { current: boolean },
  setData: (data: LinkPreviewData) => void,
) {
  try {
    const retryRes = await fetch(
      `/api/link-preview?url=${encodeURIComponent(normalizedUrl)}`,
      { cache: "no-store" },
    );

    if (!retryRes.ok) return;
    const retryJson = (await retryRes.json()) as LinkPreviewData;

    if (isMountedRef.current) setData(retryJson);
  } catch {
    // ignore retry errors
  }
}

async function fetchLinkPreview(
  normalizedUrl: string,
  signal: AbortSignal,
  isMountedRef: { current: boolean },
  retriedRef: { current: boolean },
  setData: (data: LinkPreviewData) => void,
) {
  const res = await fetch(
    `/api/link-preview?url=${encodeURIComponent(normalizedUrl)}`,
    { cache: "no-store", signal },
  );

  if (!res.ok) throw new Error("Failed to fetch preview");

  const json = (await res.json()) as LinkPreviewData;

  if (!isMountedRef.current) return;
  setData(json);

  if (json.partial && !retriedRef.current) {
    retriedRef.current = true;
    setTimeout(() => {
      void retryFetchPreview(normalizedUrl, isMountedRef, setData);
    }, 800);
  }
}

function normalizeUrl(url: string): string {
  return url.startsWith("http") ? url : `https://${url}`;
}

function getOrNull<T>(val: T | null | undefined): T | null {
  return val ?? null;
}

function getOrFallback<T>(val: T | null | undefined, fallback: T): T {
  return val ?? fallback;
}

function resolvePreviewDisplay(data: LinkPreviewData | null, hostname: string) {
  return {
    description: getOrNull(data?.description),
    favicon: getOrNull(data?.favicon),
    siteName: getOrFallback(data?.siteName, hostname),
    title: getOrFallback(data?.title, hostname),
  };
}

function renderPreviewBody(
  display: ReturnType<typeof resolvePreviewDisplay>,
  hostname: string,
  error: string | null,
) {
  return (
    <div className="p-3 min-w-0">
      <div className="flex items-center gap-2 text-xs text-default-500 mb-1">
        {display.favicon && (
          // eslint-disable-next-line @next/next/no-img-element
          <img alt="favicon" className="w-4 h-4" src={display.favicon} />
        )}
        <span className="truncate" title={hostname}>
          {display.siteName}
        </span>
      </div>
      <div className="text-sm font-medium truncate" title={display.title}>
        {display.title}
      </div>
      {display.description && (
        <div className="text-xs text-default-600 line-clamp-2 mt-1">
          {display.description}
        </div>
      )}
      {error && (
        <div className="text-xs text-danger mt-1">Preview unavailable</div>
      )}
    </div>
  );
}

export function HelpfulLinkPreview({ url }: HelpfulLinkPreviewProps) {
  const normalizedUrl = normalizeUrl(url);
  const [data, setData] = useState<LinkPreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const retriedRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();

    retriedRef.current = false;
    void fetchLinkPreview(
      normalizedUrl,
      controller.signal,
      isMountedRef,
      retriedRef,
      setData,
    ).catch((err: Error) => {
      if (isMountedRef.current) setError(err.message);
    });

    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, [normalizedUrl]);

  const hostname = getHostname(normalizedUrl);
  const initials = getDomainInitials(hostname);
  const hue = hashStringToHue(hostname);
  const gradientStyle: React.CSSProperties = {
    background: `linear-gradient(135deg, hsl(${hue} 90% 92%), hsl(${(hue + 40) % 360} 90% 85%))`,
  };
  const display = resolvePreviewDisplay(data, hostname);

  return (
    <a
      className="block border border-default-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
      data-testid="helpful-link-preview"
      href={normalizedUrl}
      rel="noopener noreferrer"
      target="_blank"
    >
      <div className="flex">
        {renderPreviewImage(data, hostname, initials, gradientStyle)}
        {renderPreviewBody(display, hostname, error)}
      </div>
    </a>
  );
}
